import type { OfficeDb } from './db.js'
import { makeMutation, type Mutation } from './outbox.js'

/**
 * Every write goes through here.
 *
 * Two things happen atomically: the local mirror is updated so the UI responds
 * instantly, and a mutation is appended to the outbox for replay. The network is
 * never on the critical path of a user action — that is what makes the tablet
 * usable with the internet down.
 *
 * `now` is always passed in from the TrustedClock. Nothing here reads the
 * ambient device clock (docs/02 §4).
 */
export interface MutationContext {
  db: OfficeDb
  now: () => number
  actorId: string
}

/**
 * `localWrite` is handed THE MUTATION'S OWN ID, and any row it inserts must use
 * it.
 *
 * The server writes append-only rows keyed on that same id (`sync_push` inserts
 * `presence_events(id, ...)` as `m_id`). A local row minted with a fresh
 * `crypto.randomUUID()` is therefore a DIFFERENT row from the one the server
 * stores, so the pull that follows adds the server's copy alongside it instead
 * of overwriting it -- one tap on the wall, two check-ins in the mirror, forever
 * and growing. Sharing the id makes the optimistic write and the pulled row the
 * same row, which is what makes the optimistic write safe at all.
 */
async function enqueue(
  ctx: MutationContext,
  kind: string,
  payload: Record<string, unknown>,
  localWrite?: (db: OfficeDb, id: string) => Promise<void>,
): Promise<Mutation> {
  const m = makeMutation(kind, payload, ctx.now())
  await ctx.db.transaction('rw', ctx.db.tables, async () => {
    if (localWrite) await localWrite(ctx.db, m.id)
    await ctx.db.outbox.put(m)
  })
  return m
}

export const mutations = {
  checkIn: (ctx: MutationContext, personId: string, source = 'tablet_tap') =>
    enqueue(ctx, 'presence.event',
      { personId, type: 'in', at: new Date(ctx.now()).toISOString(), source },
      async (db, id) => { await db.presence_events.put({
        id, person_id: personId, type: 'in',
        at: new Date(ctx.now()).toISOString(), source }) }),

  checkOut: (ctx: MutationContext, personId: string, source = 'tablet_tap') =>
    enqueue(ctx, 'presence.event',
      { personId, type: 'out', at: new Date(ctx.now()).toISOString(), source },
      async (db, id) => { await db.presence_events.put({
        id, person_id: personId, type: 'out',
        at: new Date(ctx.now()).toISOString(), source }) }),

  completeChore: (ctx: MutationContext, instanceId: string) =>
    enqueue(ctx, 'chore.complete',
      { instanceId, at: new Date(ctx.now()).toISOString(), by: ctx.actorId },
      async db => { await db.chore_instances.update(instanceId, {
        status: 'done', completed_at: new Date(ctx.now()).toISOString(), completed_by: ctx.actorId }) }),

  uncompleteChore: (ctx: MutationContext, instanceId: string) =>
    enqueue(ctx, 'chore.uncomplete', { instanceId },
      async db => { await db.chore_instances.update(instanceId, {
        status: 'pending', completed_at: null, completed_by: null }) }),

  requestSwap: (ctx: MutationContext, instanceId: string, to: string | null, reason: string | null) =>
    enqueue(ctx, 'chore.swap_request',
      { instanceId, from: ctx.actorId, to: to ?? '', reason, at: new Date(ctx.now()).toISOString() }),

  acceptSwap: (ctx: MutationContext, swapId: string) =>
    enqueue(ctx, 'chore.swap_accept',
      { swapId, by: ctx.actorId, at: new Date(ctx.now()).toISOString() }),

  createBooking: (ctx: MutationContext, b: {
    resourceId: string; title: string; startsAt: string; endsAt: string; kind?: string
  }) => enqueue(ctx, 'booking.create', { ...b, createdBy: ctx.actorId, createdAt: new Date(ctx.now()).toISOString() }),

  cancelBooking: (ctx: MutationContext, bookingId: string) =>
    enqueue(ctx, 'booking.cancel', { bookingId },
      async db => { await db.bookings.update(bookingId, { status: 'cancelled' }) }),

  startOccupancy: (ctx: MutationContext, resourceId: string) =>
    enqueue(ctx, 'occupancy.start',
      { resourceId, by: ctx.actorId, at: new Date(ctx.now()).toISOString(), source: 'manual' }),

  endOccupancy: (ctx: MutationContext, resourceId: string) =>
    enqueue(ctx, 'occupancy.end', { resourceId, at: new Date(ctx.now()).toISOString() }),

  joinQueue: (ctx: MutationContext, resourceId: string, personId = ctx.actorId) =>
    enqueue(ctx, 'queue.join', { resourceId, personId, at: new Date(ctx.now()).toISOString() }),

  leaveQueue: (ctx: MutationContext, entryId: string) =>
    enqueue(ctx, 'queue.leave', { entryId, at: new Date(ctx.now()).toISOString() }),

  claimQueue: (ctx: MutationContext, entryId: string) =>
    enqueue(ctx, 'queue.claim', { entryId, at: new Date(ctx.now()).toISOString() }),

  recordMatch: (ctx: MutationContext, m: {
    resourceId: string; kind: 'singles' | 'doubles'
    sideA: string[]; sideB: string[]; scoreA: number; scoreB: number
  }) => enqueue(ctx, 'match.record', {
    ...m, winner: m.scoreA > m.scoreB ? 'a' : 'b',
    playedAt: new Date(ctx.now()).toISOString(), recordedBy: ctx.actorId }),

  /**
   * `supersedesId` is a TOP-LEVEL field because `sync_push` reads it as
   * `d->>'supersedesId'` and lifts it onto the `proposals.supersedes_id`
   * column. Burying it inside `payload` — where it looks like it belongs, next
   * to the counter-proposal it came from — meant the server read null and the
   * link between a counter-proposal and the proposal it produced was dropped
   * on every sync (docs/01 §4.3: the decision log has to stay honest about
   * what was actually proposed).
   */
  createProposal: (ctx: MutationContext, p: {
    typeKey: string; title: string; details?: string
    payload?: Record<string, unknown>; autoApproveAt?: string | null
    supersedesId?: string | null
  }) => enqueue(ctx, 'proposal.create', {
    typeKey: p.typeKey, title: p.title, details: p.details ?? null,
    payload: p.payload ?? {}, autoApproveAt: p.autoApproveAt ?? '',
    supersedesId: p.supersedesId ?? '',
    createdBy: ctx.actorId, createdAt: new Date(ctx.now()).toISOString() }),

  vote: (ctx: MutationContext, proposalId: string, vote: 'approve' | 'reject',
         comment?: string, counterProposal?: Record<string, unknown>) =>
    enqueue(ctx, 'proposal.vote', {
      proposalId, voterId: ctx.actorId, vote, comment: comment ?? null,
      counterProposal: counterProposal ?? null, at: new Date(ctx.now()).toISOString() }),

  cancelProposal: (ctx: MutationContext, proposalId: string) =>
    enqueue(ctx, 'proposal.cancel', { proposalId, at: new Date(ctx.now()).toISOString() }),

  flagSupply: (ctx: MutationContext, itemId: string, state: 'ok' | 'low' | 'out') =>
    enqueue(ctx, 'supply.flag', { itemId, state, at: new Date(ctx.now()).toISOString(), by: ctx.actorId },
      async db => { await db.supply_items.update(itemId, { state }) }),

  addExpense: (ctx: MutationContext, e: {
    description: string; amount: number; category?: string
    splitKind?: string; splits: { personId: string; shareAmount: number; settledImmediately: boolean }[]
  }) => enqueue(ctx, 'expense.create', {
    ...e, paidBy: ctx.actorId, spentAt: new Date(ctx.now()).toISOString(),
    createdAt: new Date(ctx.now()).toISOString() }),

  payBill: (ctx: MutationContext, billInstanceId: string, amount: number, method = 'cash') =>
    enqueue(ctx, 'bill.payment', {
      billInstanceId, amount, method, paidBy: ctx.actorId,
      paidAt: new Date(ctx.now()).toISOString() }),

  settle: (ctx: MutationContext, to: string, amount: number, method = 'cash') =>
    enqueue(ctx, 'settlement.create', {
      from: ctx.actorId, to, amount, method, at: new Date(ctx.now()).toISOString() }),

  addNote: (ctx: MutationContext, body: string, colour?: string, pinnedToRoomId?: string) =>
    enqueue(ctx, 'note.create', {
      authorId: ctx.actorId, body, colour: colour ?? '#ffd966',
      pinnedToRoomId: pinnedToRoomId ?? '', createdAt: new Date(ctx.now()).toISOString() }),

  deleteNote: (ctx: MutationContext, noteId: string) =>
    enqueue(ctx, 'note.delete', { noteId }, async db => { await db.notes.delete(noteId) }),

  setSpaceMode: (ctx: MutationContext, mode: 'work' | 'chill' | 'gaming' | 'empty', auto = false) =>
    enqueue(ctx, 'spacemode.set', {
      mode, by: auto ? '' : ctx.actorId, auto, at: new Date(ctx.now()).toISOString() }),

  savePlan: (ctx: MutationContext, planId: string, name: string, doc: unknown, version: number) =>
    enqueue(ctx, 'plan.save', {
      planId, name, doc, version, updatedAt: new Date(ctx.now()).toISOString(), updatedBy: ctx.actorId }),
}

export type Mutations = typeof mutations
